/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _sortBy from 'lodash/sortBy';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { Message } from '@paypalcorp/worldready-react';
import {
  AutoSuggest,
  CaptionText,
  Col,
  Container,
  HeadingText,
  Icon,
  Row,
  Sheet,
  SuggestionList,
} from '@paypalcorp/pp-react';
import { CHAT_BOT, CONTACT_TOPIC_TREE_INTENTS, POST_CHAT_BOT_ELIGIBILITY } from '../../common/storeConstants';
import {
  doServerSideFptiCall,
  finalIntentSelected,
  ocpPromptCardSelected,
  searchSheetClose,
} from './TopicIntents-Actions';
import { getSearchResult, topicSearchPool } from './TopicIntentsSearchHelper';
import { generateLinkData, trackActivityEvent, trackClickEvent } from '../../helpers/analytics/fptiHelper';
import { intents, intentType, pageNames } from '../../common/constants';
import { fetchTopicData } from '../ContactOption/ContactOptionsAction';
import { redirectLocation } from '../../../js/contactUs/actions/action-contact-location';
import { FPTI_DATA } from '../../common/propTypes';
import { handlePostChatBotEligibility, isLoggedIn } from '../../helpers/twHelper';
import { resetCallUsInfo } from '../callUsComponent/CallUs-Actions';
import { getC3MarketChannelEligibility } from '../../../js/lib/globalHelper';
import { IDIChannelMapping } from '../../../js/lib/asyncMessageHelper';
import { handleChatBotPromptOrBtnShownFPTIEvent } from '../../helpers/chatBotHelper';
import { updateContext } from '../../helpers/omniChannel/omniChannelHelper';
import TopicIntentsBlobs from './TopicIntentsBlobs';

const TopicIntentSearchSheet = function (props) {
  const dispatch = useDispatch();
  const { searchSheetOpen, priorityIntents, topicIntents, isOcpEnabled, ocp } = useSelector(
    (state) => state[CONTACT_TOPIC_TREE_INTENTS],
  );
  const { chatBotDisplay } = useSelector((state) => state[POST_CHAT_BOT_ELIGIBILITY]);
  const { chatBotShow: chatBotShowAfterSpinner } = useSelector((state) => state[CHAT_BOT]);
  const currentTopicTreeData = useSelector((state) => state[CONTACT_TOPIC_TREE_INTENTS]);
  const { selectedFinalIntent } = useSelector((state) => state[CONTACT_TOPIC_TREE_INTENTS]);
  const { FPTIData } = props;
  const [suggestions, setSuggestions] = useState([]);
  const [value, setValue] = useState('');
  let searchPool = [];

  useEffect(() => {
    if (searchSheetOpen) {
      trackActivityEvent(FPTIData.topicTreePriorityIntentSearchSheetOpen);
    }
  }, [searchSheetOpen]);

  const getSuggestions = (query) => {
    const searchResults = getSearchResult({ query, searchPool });
    return JSON.parse(JSON.stringify(searchResults));
  };

  const handleChange = (evt) => {
    setValue(evt.target.value);
  };

  const handleSearchBarClickEvent = (e) => {
    e.preventDefault();
    trackClickEvent({ ...FPTIData.topicTreeSearchBarClick });
  };

  const handleClear = () => {
    setValue('');
    setSuggestions([]);
  };

  const handleSelect = (evt, suggestion, priorityIntentClick) => {
    if (!_isEmpty(suggestion)) {
      const selectedIntent = suggestion;
      const { ref_id: intentCode, topic_id: topicId } = selectedIntent;
      let fptiClickData;
      if (priorityIntentClick) {
        fptiClickData = {
          ...FPTIData.topicTreePriorityIntentClick,
          ...{
            link: generateLinkData(FPTIData.topicTreePriorityIntentClick.link, { intentCode }),
          },
        };
      } else {
        fptiClickData = {
          ...FPTIData.topicTreeSuggestedIntentClick,
          ...{
            link: generateLinkData(FPTIData.topicTreeSuggestedIntentClick.link, { intentCode }),
          },
        };
      }
      trackClickEvent(fptiClickData);
      // fire final intent selected action
      dispatch(finalIntentSelected(selectedIntent));

      // Chatbot service deprecated - removed postChatBotEligibility call

      // close search sheet action && redirect from search page to contactUs page
      dispatch(searchSheetClose());
      dispatch(redirectLocation({ pageName: pageNames.CONTACT_US }));

      // fetch topic data because the final intent is changed
      dispatch(fetchTopicData(topicId));

      // reset call us because the topic intent is changed
      dispatch(resetCallUsInfo());
    }
    return null;
  };

  const priorityIntentClick = (evt, topicsIntents) => {
    handleSelect(evt, topicsIntents, true);
  };

  const fireChatbotFPTIOnSheetClose = () => {
    if (chatBotDisplay && chatBotShowAfterSpinner && getC3MarketChannelEligibility(IDIChannelMapping.CHAT_BOT)) {
      handleChatBotPromptOrBtnShownFPTIEvent({ finalIntentRefId: selectedFinalIntent.ref_id });
    }
  };

  // Handle the OCP Prompt click
  const ocpPromptClickHandler = (e, selectedOcpPromptIntent) => {
    e.preventDefault();

    const selectedOcpArticle = ocp.intentDetails.find((ocpCard) => {
      return selectedOcpPromptIntent.intentCode === _get(ocpCard, 'channelDetails.intentCode', '');
    });

    if (selectedOcpArticle) {
      doServerSideFptiCall(selectedOcpArticle);

      currentTopicTreeData.selectedOcpPrompt = selectedOcpArticle;
      dispatch(ocpPromptCardSelected(currentTopicTreeData));

      // update-context call
      const updateContextObj = {
        articleContent: selectedOcpArticle,
        icfId: '',
        customerConfirmed: true,
      };
      updateContext(updateContextObj);

      dispatch(searchSheetClose(currentTopicTreeData));
    }
  };

  const onCloseSheetHandler = () => {
    if (searchSheetOpen) {
      trackClickEvent({ ...FPTIData.topicTreePriorityIntentSearchSheetClose });
      handleClear();
      // fire search sheet close action
      dispatch(searchSheetClose());
      // fire Chatbot FPTI event when sheet is close
      fireChatbotFPTIOnSheetClose();
    }
  };

  const renderOcpPrompts = () => {
    return ocp.intentDetails.map((ocpCard) => {
      const intent = _get(ocpCard, 'channelDetails', '');
      intent.ref_id = intent.intentCode;

      return (
        <Col key={intent.ref_id}>
          <TopicIntentsBlobs intent={intent} onClickHandler={ocpPromptClickHandler} />
        </Col>
      );
    });
  };

  const renderTopics = () => {
    const sortedTopicIntents = _sortBy(priorityIntents, 'priority');
    let filteredIntents = sortedTopicIntents;
    // Filter out the priority intents which are already being shown as omni intents
    if (isOcpEnabled) {
      filteredIntents = sortedTopicIntents.filter(
        (sortedtIntent) =>
          !ocp.intentDetails.find((ocpIntent) => sortedtIntent.intent_code === ocpIntent.channelDetails.intentCode),
      );
    }

    return filteredIntents.map((intent) => (
      <Col key={intent.ref_id}>
        <TopicIntentsBlobs intent={intent} component={intents.SEARCH} onClickHandler={priorityIntentClick} />
      </Col>
    ));
  };

  const suggestionList = (
    <SuggestionList
      suggestions={suggestions}
      query={value}
      onSelect={handleSelect}
      data-testid="sh-topic-intent-search-suggest-list"
    />
  );

  const fetchSuggestionsHandler = (input) => {
    const newSuggestions = getSuggestions(input);
    setSuggestions((prevSuggestions) => {
      if (!_isEqual(prevSuggestions, newSuggestions)) {
        if (!_isEmpty(newSuggestions)) {
          // fire FPTI event if the search results are received
          trackActivityEvent({ ...FPTIData.topicTreeSearchSheetAutoSuggestShown });
        }
        return newSuggestions;
      }
      return prevSuggestions;
    });
  };

  const renderSearchBar = () => {
    return (
      <AutoSuggest
        id="basic"
        suggestionList={suggestionList}
        name="basic"
        label={<Message id="smarthelp.app.topic.searchsheet.searchLabel" />}
        value={value}
        fetchSuggestions={(input) => fetchSuggestionsHandler(input)}
        onChange={handleChange}
        onClear={handleClear}
        onClick={(e) => handleSearchBarClickEvent(e)}
        autoComplete="off"
        leftIcon={<Icon name="search" size="sm" />}
        rightIcon={<Icon name="close" size="sm" onClick={handleClear} />}
        inputClassName="sh_topic_intent__search_bar"
        data-testid="sh-topic-intent-search-bar"
      />
    );
  };

  const renderSearchSheet = () => {
    if (!_isEmpty(topicIntents)) {
      searchPool = topicSearchPool(topicIntents.contact_email_template);
    }

    return (
      <Sheet
        isOpen={searchSheetOpen}
        onCloseComplete={onCloseSheetHandler}
        title={<Message id="smarthelp.app.topic.searchsheet.header.label" />}
        hideTitle
        noPadding
        mobileFixedHeight="90"
        className="sh_topic_intent__search_sheet"
        data-testid="sh-topic-intent-search-sheet"
      >
        <Container
          className="sh_topic_intent__search_sheet_container"
          data-testid="sh-topic-intent-search-sheet-container"
        >
          <HeadingText
            size="md"
            className="sh_topic_intent__search_sheet_header"
            data-testid="sh-topic-intent-search-sheet-header"
          >
            <Message id="smarthelp.app.topic.searchsheet.header.label" />
          </HeadingText>

          <>{renderSearchBar()}</>

          {/* Render the 'Recommended topics for you' section when OCP data available */}
          {isLoggedIn() && isOcpEnabled && (
            <>
              <CaptionText
                className="sh_topic_intent__search_sheet_sublabel"
                data-testid="sh-topic-intent-search-sheet-sublabel-recommended"
              >
                <Message id="smarthelp.app.topic.searchsheet.sublabel.recommended" />
              </CaptionText>
              <div
                className="sh_topic_intent__search_icon_container"
                data-testid="sh-topic-intent-search-icon-container"
              >
                <Row className="sh_topic_intent__search_topics" data-testid="sh-topic-intent-search-topics">
                  {renderOcpPrompts()}
                </Row>
              </div>
            </>
          )}

          {/* Render the 'Common topics' section */}
          <CaptionText
            className="sh_topic_intent__search_sheet_sublabel"
            data-testid="sh-topic-intent-search-sheet-sublabel-common"
          >
            <Message id="smarthelp.app.topic.searchsheet.sublabel.common" />
          </CaptionText>
          <div className="sh_topic_intent__search_icon_container" data-testid="sh-topic-intent-search-icon-container">
            <Row className="sh_topic_intent__search_topics" data-testid="sh-topic-intent-search-topics">
              {renderTopics()}
            </Row>
          </div>
        </Container>
      </Sheet>
    );
  };
  return renderSearchSheet();
};

TopicIntentSearchSheet.propTypes = {
  FPTIData: PropTypes.shape(FPTI_DATA).isRequired,
};

export default TopicIntentSearchSheet;
